import { ApiError } from "../../lib/api";
import type { GuideRequirements, MyGuideStatus } from "../../lib/guide-api";

// 状態別ランディングの判定（tst-00cu.5）。React に依存しない純関数で、vitest が直接検証する。
//
// guide-web は「応募〜本登録」専用で、ログイン後の居場所は guide.status で決まる:
//   未提出（guide 行なし / cognito_sub 未バインド / Applying） → 本登録ウィザード
//   提出済み・承認待ち（Onboarding）                          → 審査中ビュー
//   Active                                                     → guide-app への橋
//   Inactive / Terminated / 業務停止（session-guard の 403）  → 案内文のみ
//
// 以前は SignIn の既定遷移先が /onboarding 固定で、/onboarding は状態を見ずに常にウィザード 1 から
// 始まった。提出済みのアカウントが再送信して 409「already registered」を踏み、承認待ちの居場所も
// Active 後に guide-app へ渡す橋も無かった。
//
// 保険証券を求めるかどうか（GuideRequirements.insuranceRequired / tst-szv6.5）も居場所と一緒に運ぶ。
// 保留中（false・既定）はウィザードが保険のステップを出さず、審査中ビューは保険の行を出さない。

/** GET /guides/me/status の結果を、HTTP の文脈ごと 1 つの値に畳んだもの。 */
export type StatusProbe =
  | {
      kind: "found";
      status: string;
      insurance: MyGuideStatus["insurance"];
      contract: MyGuideStatus["contract"];
    }
  /** 404 guide_not_registered — cognito_sub にバインドされた guide 行が無い。 */
  | { kind: "not_found" }
  /** 403 — session-guard（guide_inactive）か guide グループ外（guide_only）。 */
  | { kind: "forbidden"; error: string | null }
  /** ネットワーク断・5xx・401（api() が signOut 済み）など、状態が判定できない。 */
  | { kind: "unavailable" };

/** 審査中ビューに出す保険証券の状態。 */
export type InsuranceState = "not_submitted" | "pending" | "approved" | "rejected";

/** 案内文だけのビューに出す理由。 */
export type IneligibleReason =
  | "inactive" // 休止（guide.status=Inactive）
  | "terminated" // 契約終了（guide.status=Terminated）
  | "barred" // session-guard が止めた（退会・休止・業務停止のいずれか。API は区別を返さない）
  | "forbidden" // guide グループ外など、このアカウントではガイド登録を扱えない
  | "unknown_status"; // 想定外の status 値

export type Landing =
  /** insuranceRequired=false なら保険証券のステップを出さない。 */
  | { view: "wizard"; insuranceRequired: boolean }
  /** insurance=null は「保険証券を求めていない」（保留中）＝状態の行そのものを出さない。 */
  | { view: "pending"; insurance: InsuranceState | null }
  | { view: "active" }
  | { view: "ineligible"; reason: IneligibleReason }
  | { view: "error" };

/** 最後に出した証券の審査状態（Pending / Approved / Rejected）→ 表示状態。未提出は null。 */
export function insuranceState(insurance: MyGuideStatus["insurance"]): InsuranceState {
  if (!insurance) return "not_submitted";
  switch (insurance.verificationStatus) {
    case "Approved":
      return "approved";
    case "Rejected":
      return "rejected";
    default:
      return "pending";
  }
}

/** 状態 + 求める書類 → 居場所。 */
export function resolveLanding(probe: StatusProbe, requirements: GuideRequirements): Landing {
  const { insuranceRequired } = requirements;
  switch (probe.kind) {
    case "not_found":
      return { view: "wizard", insuranceRequired };
    case "unavailable":
      return { view: "error" };
    case "forbidden":
      return {
        view: "ineligible",
        reason: probe.error === "guide_inactive" ? "barred" : "forbidden",
      };
    case "found":
      switch (probe.status) {
        case "Applying":
          // 応募採用時に先行作成された行（cognito_sub 未バインド）。本登録はこれから。
          return { view: "wizard", insuranceRequired };
        case "Onboarding":
          return {
            view: "pending",
            insurance: insuranceRequired ? insuranceState(probe.insurance) : null,
          };
        case "Active":
          return { view: "active" };
        case "Inactive":
          return { view: "ineligible", reason: "inactive" };
        case "Terminated":
          return { view: "ineligible", reason: "terminated" };
        default:
          return { view: "ineligible", reason: "unknown_status" };
      }
  }
}

/** ウィザード提出直後の居場所。サーバを読み直さなくても審査中ビューに置ける（再送信の 409 を構造的に防ぐ）。 */
export function landingAfterSubmit(
  result: { insuranceSubmitted: boolean },
  requirements: GuideRequirements,
): Landing {
  if (!requirements.insuranceRequired) return { view: "pending", insurance: null };
  return { view: "pending", insurance: result.insuranceSubmitted ? "pending" : "not_submitted" };
}

/** GET /guides/me/status の正常応答 → StatusProbe。 */
export function probeFromStatus(status: MyGuideStatus): StatusProbe {
  return {
    kind: "found",
    status: status.status,
    insurance: status.insurance,
    contract: status.contract,
  };
}

/** fetchMyGuideStatus の例外 → StatusProbe。ApiError.message は body.error（無ければ "HTTP <status>"）。 */
export function probeFromError(e: unknown): StatusProbe {
  if (e instanceof ApiError) {
    if (e.status === 404) return { kind: "not_found" };
    if (e.status === 403) {
      return { kind: "forbidden", error: e.message.startsWith("HTTP ") ? null : e.message };
    }
  }
  return { kind: "unavailable" };
}
